import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListIcon from '@mui/icons-material/List';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import { useParams } from 'react-router-dom';
import {
    FunnelStage,
    FUNNEL_STAGES,
    FUNNEL_STAGE_MAP,
    SalesFunnelProspect,
    FunnelDashboardStats,
    FIYRPOD_DARK,
    FIYRPOD_GREEN,
    FIYRPOD_MUTED,
    FIYRPOD_BORDER,
} from '../salesFunnel.types';

const darkGridSx = {
    border: 'none',
    backgroundColor: 'transparent',
    color: '#d0dae8',
    '& .MuiDataGrid-row': { backgroundColor: 'transparent' },
    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(118,196,66,0.06) !important' },
    '& .MuiDataGrid-cell': { color: '#d0dae8', borderColor: FIYRPOD_BORDER, fontSize: '0.8rem' },
    '& .MuiDataGrid-virtualScroller': { backgroundColor: 'transparent' },
    '& .MuiDataGrid-virtualScrollerContent': { backgroundColor: 'transparent' },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'rgba(0,0,0,0.35)',
        borderColor: FIYRPOD_BORDER,
    },
    '& .MuiDataGrid-columnHeader': { backgroundColor: 'transparent', color: FIYRPOD_MUTED },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: FIYRPOD_MUTED,
        fontWeight: 600,
        fontSize: '0.72rem',
    },
    '& .MuiDataGrid-sortIcon': { color: FIYRPOD_GREEN, opacity: '1 !important' },
    '& .MuiDataGrid-menuIconButton': { color: FIYRPOD_MUTED },
    '& .MuiDataGrid-columnSeparator': { color: FIYRPOD_BORDER },
    '& .MuiDataGrid-footerContainer': {
        borderColor: FIYRPOD_BORDER,
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    '& .MuiTablePagination-root': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-selectLabel': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-displayedRows': { color: FIYRPOD_MUTED },
    '& .MuiTablePagination-select': { color: '#d0dae8' },
    '& .MuiTablePagination-actions .MuiIconButton-root': { color: FIYRPOD_MUTED },
    '& .MuiDataGrid-overlay': { backgroundColor: 'rgba(13,27,46,0.8)', color: FIYRPOD_MUTED },
};
import {
    getSalesFunnelProspects,
    getSalesFunnelDashboard,
    createSalesFunnelProspect,
    updateSalesFunnelProspect,
    advanceSalesFunnelProspect,
    GetProspectsParams,
} from '../salesFunnel.api';
import FunnelPipeline from '../components/salesFunnel/FunnelPipeline';
import FunnelSummaryCards from '../components/salesFunnel/FunnelSummaryCards';
import AddProspectDialog from '../components/salesFunnel/AddProspectDialog';
import ProspectDetailDrawer from '../components/salesFunnel/ProspectDetailDrawer';

const formatCurrency = (value: number | null): string => {
    if (value == null) return '--';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    }).format(value);
};

const SalesFunnelScene: React.FC = () => {
    const { id: orgUuid } = useParams<{ id: string }>();
    const [viewMode, setViewMode] = useState<'dashboard' | 'list'>('dashboard');
    const [dashboardStats, setDashboardStats] = useState<FunnelDashboardStats | null>(null);
    const [prospects, setProspects] = useState<SalesFunnelProspect[]>([]);
    const [loading, setLoading] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'updated_at', sort: 'desc' },
    ]);
    const [stageFilter, setStageFilter] = useState<FunnelStage | ''>('');
    const [searchText, setSearchText] = useState('');
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingProspect, setEditingProspect] = useState<SalesFunnelProspect | null>(null);
    const [selectedProspectUuid, setSelectedProspectUuid] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const loadDashboard = useCallback(async () => {
        if (!orgUuid) return;
        setDashboardLoading(true);
        try {
            const response = await getSalesFunnelDashboard(orgUuid);
            setDashboardStats(response.data);
        } catch (err) {
            console.error('Failed to load dashboard', err);
        } finally {
            setDashboardLoading(false);
        }
    }, [orgUuid]);

    const loadProspects = useCallback(async () => {
        if (!orgUuid) return;
        setLoading(true);
        try {
            const sortField = sortModel[0];
            const sortParam = sortField
                ? `${sortField.sort === 'desc' ? '-' : ''}${sortField.field}`
                : '-updated_at';

            const params: GetProspectsParams = {
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
                sort: sortParam,
                ...(stageFilter && { stage: stageFilter }),
                ...(searchText && { search: searchText }),
            };

            const response = await getSalesFunnelProspects(orgUuid, params);
            setProspects(response.data.data);
            setTotalRows(response.data.meta?.total || response.data.data.length);
        } catch (err) {
            console.error('Failed to load prospects', err);
        } finally {
            setLoading(false);
        }
    }, [orgUuid, paginationModel, sortModel, stageFilter, searchText]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        loadProspects();
    }, [loadProspects]);

    const handleRefresh = () => {
        loadDashboard();
        loadProspects();
    };

    const handleAddProspect = async (values: any) => {
        if (!orgUuid) return;
        await createSalesFunnelProspect(orgUuid, values);
        handleRefresh();
    };

    const handleEditProspect = async (values: any) => {
        if (!orgUuid || !editingProspect) return;
        await updateSalesFunnelProspect(orgUuid, editingProspect.uuid, values);
        setEditingProspect(null);
        handleRefresh();
    };

    const handleAdvance = async (prospectUuid: string) => {
        if (!orgUuid) return;
        try {
            await advanceSalesFunnelProspect(orgUuid, prospectUuid);
            handleRefresh();
        } catch (err) {
            console.error('Failed to advance', err);
        }
    };

    const handleStageFilterFromPipeline = (stage: FunnelStage | null) => {
        setStageFilter(stage || '');
        if (stage) {
            setViewMode('list');
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'prospect_name',
            headerName: 'Prospect Name',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        cursor: 'pointer',
                        color: FIYRPOD_GREEN,
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => {
                        setSelectedProspectUuid(params.row.uuid);
                        setDrawerOpen(true);
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'current_stage',
            headerName: 'Stage',
            width: 130,
            renderCell: (params) => {
                const config = FUNNEL_STAGE_MAP[params.value as FunnelStage];
                return (
                    <Chip
                        label={config?.label || params.value}
                        size="small"
                        sx={{
                            backgroundColor: config?.color || '#ccc',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                );
            },
        },
        {
            field: 'facility_type',
            headerName: 'Facility Type',
            width: 120,
        },
        {
            field: 'athlete_count',
            headerName: 'Athletes',
            width: 90,
            type: 'number',
        },
        {
            field: 'contact_name',
            headerName: 'Contact',
            width: 140,
        },
        {
            field: 'pipeline_value',
            headerName: 'Est. Value',
            width: 110,
            type: 'number',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'days_in_current_stage',
            headerName: 'Days in Stage',
            width: 100,
            type: 'number',
            renderCell: (params) => {
                const days = params.value as number;
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color:
                                days > 30
                                    ? 'error.main'
                                    : days > 14
                                    ? 'warning.main'
                                    : 'text.primary',
                            fontWeight: days > 30 ? 600 : 400,
                        }}
                    >
                        {days}d
                    </Typography>
                );
            },
        },
        {
            field: 'updated_at',
            headerName: 'Last Updated',
            width: 120,
            renderCell: (params) =>
                new Date(params.value).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                }),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params) => {
                const prospect = params.row as SalesFunnelProspect;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {prospect.current_stage !== 'launch_install' && !prospect.lost_at && (
                            <Tooltip title="Advance Stage">
                                <IconButton
                                    size="small"
                                    onClick={() => handleAdvance(prospect.uuid)}
                                    color="primary"
                                >
                                    <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => setEditingProspect(prospect)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* FiyrPod-branded header banner */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                    border: `1px solid ${FIYRPOD_BORDER}`,
                    borderRadius: 2,
                    px: 3,
                    py: 2,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    {/* FiyrPod leaf dot */}
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: FIYRPOD_GREEN,
                            boxShadow: `0 0 8px ${FIYRPOD_GREEN}80`,
                            flexShrink: 0,
                        }}
                    />
                    <Box>
                        <Typography
                            sx={{
                                color: FIYRPOD_GREEN,
                                fontWeight: 900,
                                fontSize: '0.6rem',
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                lineHeight: 1,
                            }}
                        >
                            FiyrPod
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#ffffff',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Sales Funnel
                        </Typography>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Tabs
                        value={viewMode}
                        onChange={(_, val) => setViewMode(val)}
                        sx={{
                            minHeight: 34,
                            '& .MuiTab-root': {
                                color: FIYRPOD_MUTED,
                                minHeight: 34,
                                fontSize: '0.72rem',
                                px: 1.5,
                                '&.Mui-selected': { color: FIYRPOD_GREEN },
                            },
                            '& .MuiTabs-indicator': { backgroundColor: FIYRPOD_GREEN },
                        }}
                    >
                        <Tab
                            value="dashboard"
                            icon={<DashboardIcon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            label="Dashboard"
                        />
                        <Tab
                            value="list"
                            icon={<ListIcon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            label="All Prospects"
                        />
                    </Tabs>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddDialogOpen(true)}
                        size="small"
                        sx={{
                            backgroundColor: FIYRPOD_GREEN,
                            color: '#0d1b2e',
                            fontWeight: 700,
                            '&:hover': { backgroundColor: '#8ed44e' },
                        }}
                    >
                        Add Prospect
                    </Button>
                </Box>
            </Box>

            {/* Pipeline visualization — always visible */}
            {dashboardStats && (
                <FunnelPipeline
                    stageCounts={dashboardStats.stage_counts}
                    selectedStage={stageFilter || null}
                    onStageClick={handleStageFilterFromPipeline}
                />
            )}

            {/* Dashboard View */}
            {viewMode === 'dashboard' && (
                <>
                    {dashboardLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : dashboardStats ? (
                        <>
                            <FunnelSummaryCards stats={dashboardStats} />

                            {/* Two-column layout on desktop */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' },
                                    gap: 3,
                                }}
                            >
                                {/* Stage Breakdown */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                                        border: `1px solid ${FIYRPOD_BORDER}`,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 3,
                                            py: 1.5,
                                            borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                backgroundColor: FIYRPOD_GREEN,
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: FIYRPOD_GREEN,
                                                fontWeight: 700,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                            }}
                                        >
                                            Stage Breakdown
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2.5 }}>
                                        {FUNNEL_STAGES.map((stage) => {
                                            const count =
                                                dashboardStats.stage_counts[stage.key] || 0;
                                            const total = dashboardStats.total_prospects || 1;
                                            const pct = ((count / total) * 100).toFixed(1);
                                            const avgDays =
                                                dashboardStats.avg_days_per_stage[stage.key];

                                            return (
                                                <Box
                                                    key={stage.key}
                                                    onClick={() =>
                                                        handleStageFilterFromPipeline(stage.key)
                                                    }
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        py: 1,
                                                        borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                                                        cursor: 'pointer',
                                                        borderRadius: 1,
                                                        px: 1,
                                                        mx: -1,
                                                        '&:last-child': { borderBottom: 'none' },
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(118,196,66,0.06)',
                                                        },
                                                    }}
                                                >
                                                    <Chip
                                                        label={stage.label}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: `${stage.color}25`,
                                                            color: stage.color,
                                                            border: `1px solid ${stage.color}60`,
                                                            fontWeight: 700,
                                                            width: 110,
                                                            fontSize: '0.68rem',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            width: 32,
                                                            textAlign: 'right',
                                                            fontWeight: 800,
                                                            flexShrink: 0,
                                                            mx: 1,
                                                            color: FIYRPOD_GREEN,
                                                        }}
                                                    >
                                                        {count}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, mx: 1.5 }}>
                                                        <Box
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 4,
                                                                backgroundColor:
                                                                    'rgba(255,255,255,0.06)',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${pct}%`,
                                                                    backgroundColor: stage.color,
                                                                    borderRadius: 4,
                                                                    transition: 'width 0.5s ease',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            width: 40,
                                                            textAlign: 'right',
                                                            flexShrink: 0,
                                                            color: FIYRPOD_MUTED,
                                                        }}
                                                    >
                                                        {pct}%
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            width: 72,
                                                            textAlign: 'right',
                                                            flexShrink: 0,
                                                            color: FIYRPOD_MUTED,
                                                        }}
                                                    >
                                                        {avgDays != null ? `~${avgDays}d avg` : '—'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>

                                {/* Recent Activity */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                                        border: `1px solid ${FIYRPOD_BORDER}`,
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 3,
                                            py: 1.5,
                                            borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                backgroundColor: FIYRPOD_GREEN,
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: FIYRPOD_GREEN,
                                                fontWeight: 700,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                            }}
                                        >
                                            Recent Activity
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            maxHeight: 480,
                                            px: 2.5,
                                            py: 1,
                                        }}
                                    >
                                        {dashboardStats.recent_activity.length > 0 ? (
                                            dashboardStats.recent_activity.map((log) => (
                                                <Box
                                                    key={log.uuid}
                                                    sx={{
                                                        py: 1.25,
                                                        borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                                                        '&:last-child': { borderBottom: 'none' },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500, color: '#e0e8f0' }}
                                                    >
                                                        {log.description}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: FIYRPOD_MUTED }}
                                                    >
                                                        {log.user?.name || 'System'} &middot;{' '}
                                                        {new Date(log.created_at).toLocaleString(
                                                            'en-US',
                                                            {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                            },
                                                        )}
                                                    </Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: FIYRPOD_MUTED, py: 2 }}
                                            >
                                                No activity yet. Add your first prospect to get
                                                started.
                                            </Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                        </>
                    ) : null}
                </>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                        border: `1px solid ${FIYRPOD_BORDER}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    {/* Filters */}
                    <Box
                        sx={{
                            p: 2,
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            borderBottom: `1px solid ${FIYRPOD_BORDER}`,
                        }}
                    >
                        <TextField
                            size="small"
                            label="Search prospects"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{
                                width: 260,
                                '& .MuiOutlinedInput-root': {
                                    color: '#d0dae8',
                                    '& fieldset': { borderColor: FIYRPOD_BORDER },
                                    '&:hover fieldset': { borderColor: FIYRPOD_GREEN },
                                    '&.Mui-focused fieldset': { borderColor: FIYRPOD_GREEN },
                                },
                                '& .MuiInputLabel-root': { color: FIYRPOD_MUTED },
                                '& .MuiInputLabel-root.Mui-focused': { color: FIYRPOD_GREEN },
                            }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Stage"
                            value={stageFilter}
                            onChange={(e) => setStageFilter(e.target.value as FunnelStage | '')}
                            sx={{
                                width: 180,
                                '& .MuiOutlinedInput-root': {
                                    color: '#d0dae8',
                                    '& fieldset': { borderColor: FIYRPOD_BORDER },
                                    '&:hover fieldset': { borderColor: FIYRPOD_GREEN },
                                    '&.Mui-focused fieldset': { borderColor: FIYRPOD_GREEN },
                                },
                                '& .MuiInputLabel-root': { color: FIYRPOD_MUTED },
                                '& .MuiInputLabel-root.Mui-focused': { color: FIYRPOD_GREEN },
                                '& .MuiSelect-icon': { color: FIYRPOD_MUTED },
                            }}
                        >
                            <MenuItem value="">All Stages</MenuItem>
                            {FUNNEL_STAGES.map((s) => (
                                <MenuItem key={s.key} value={s.key}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {stageFilter && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setStageFilter('')}
                                sx={{ color: FIYRPOD_MUTED, '&:hover': { color: FIYRPOD_GREEN } }}
                            >
                                Clear filter
                            </Button>
                        )}
                        <Typography variant="caption" sx={{ ml: 'auto', color: FIYRPOD_MUTED }}>
                            {totalRows} prospects
                        </Typography>
                    </Box>

                    <DataGridPremium
                        rows={prospects}
                        columns={columns}
                        getRowId={(row) => row.uuid}
                        loading={loading}
                        rowCount={totalRows}
                        paginationMode="server"
                        sortingMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        pageSizeOptions={[10, 25, 50]}
                        disableRowSelectionOnClick
                        autoHeight
                        sx={darkGridSx}
                    />
                </Paper>
            )}

            {/* Add Prospect Dialog */}
            <AddProspectDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onSubmit={handleAddProspect}
            />

            {/* Edit Prospect Dialog */}
            <AddProspectDialog
                open={!!editingProspect}
                onClose={() => setEditingProspect(null)}
                onSubmit={handleEditProspect}
                prospect={editingProspect}
            />

            {/* Prospect Detail Drawer */}
            <ProspectDetailDrawer
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedProspectUuid(null);
                }}
                prospectUuid={selectedProspectUuid}
                orgUuid={orgUuid}
                onProspectUpdated={handleRefresh}
            />
        </Box>
    );
};

export default SalesFunnelScene;
